import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  AuditoriaRecurso,
  AuditoriaUsuario,
  listarAuditoriaUsuarioRecurso,
  obterAuditoriaUsuario,
} from "../../../services/auditoriaUsuarios";
import { formatarDataBrasilia } from "../../../utils/formatDate";

export const permissoesLabels: Record<string, string> = {
  ADMTENANT: "Administrador da organização",
  ADM: "Administrador",
  EDITOR: "Editor",
  LEITOR: "Leitor",
  PUBLICO: "Público",
  PUBLICOWIKI: "Público Wiki",
  POR_CAMADA: "Acesso por camada",
  SEM_VINCULO: "Sem vínculo com projetos ou camadas",
};
const columns: Record<AuditoriaRecurso, { key: string; label: string }[]> = {
  projetos: [
    { key: "id_projetos", label: "ID" },
    { key: "nome", label: "Projeto" },
    { key: "ativo", label: "Situação" },
    { key: "permissao", label: "Permissão" },
  ],
  camadas: [
    { key: "projeto", label: "Projeto" },
    { key: "camada", label: "Camada" },
    { key: "permissao", label: "Permissão específica" },
    { key: "ativo", label: "Situação" },
    { key: "data_criacao", label: "Criação do vínculo" },
    { key: "data_modificacao", label: "Modificação do vínculo" },
  ],
  acessos: [
    { key: "data", label: "Data e hora" },
    { key: "sucesso", label: "Resultado" },
    { key: "ip", label: "IP" },
    { key: "erro", label: "Motivo" },
  ],
};
export default function DetalhesUsuario({
  usuario,
  scope,
  onClose,
}: {
  usuario: AuditoriaUsuario;
  scope: string[];
  onClose: () => void;
}) {
  const [recurso, setRecurso] = useState<AuditoriaRecurso>("projetos");
  const [page, setPage] = useState(0);
  const detail = useQuery({
    queryKey: ["auditoria-usuario-detalhe", ...scope, usuario.id_usuarios],
    queryFn: ({ signal }) => obterAuditoriaUsuario(usuario.id_usuarios, signal),
    staleTime: 60000,
  });
  const query = useQuery({
    queryKey: ["auditoria-usuario-detalhe", ...scope, usuario.id_usuarios, recurso, page],
    queryFn: ({ signal }) => listarAuditoriaUsuarioRecurso(usuario.id_usuarios, recurso, page * 10, signal),
    staleTime: 60000,
  });
  const current = detail.data;
  const items = query.data?.data ?? [];
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="auditoria-usuario-titulo">
      <DialogTitle id="auditoria-usuario-titulo">
        Auditoria de {current?.nome || usuario.nome || `usuário #${usuario.id_usuarios}`}
      </DialogTitle>
      <DialogContent>
        {detail.isLoading && <CircularProgress size={24} aria-label="Carregando usuário" />}
        {detail.error && (
          <Alert severity="error">Não foi possível carregar o usuário. Feche os detalhes e tente novamente.</Alert>
        )}
        {current && (
          <Box sx={{ mb: 2 }}>
            <Typography color="text.secondary">
              #{current.id_usuarios} · {current.email}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={current.ativo ? "Ativo" : "Inativo"}
                color={current.ativo ? "success" : "default"}
              />
              <Chip
                size="small"
                label={current.administrador ? "Administrador da organização" : "Permissões por projeto e camada"}
              />
              {current.tipo && <Chip size="small" variant="outlined" label={current.tipo} />}
            </Box>
          </Box>
        )}
        <Tabs
          value={recurso}
          onChange={(_, value) => {
            setRecurso(value);
            setPage(0);
          }}
          aria-label="Informações do usuário"
        >
          <Tab value="projetos" label="Projetos e permissões" />
          <Tab value="camadas" label="Permissões específicas de camadas" />
          <Tab value="acessos" label="Acessos registrados" />
        </Tabs>
        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
          {recurso === "projetos"
            ? "Consulte os projetos vinculados ao usuário. Administradores da organização têm permissão em todos os projetos."
            : recurso === "camadas"
              ? "Estes vínculos concedem permissões adicionais às permissões dos projetos. As datas se referem ao vínculo com a camada."
              : "Acessos associados ao e-mail atual nesta organização. Registros antigos sem organização identificada não aparecem aqui."}
        </Typography>
        {query.isLoading && (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <CircularProgress aria-label="Carregando detalhes" />
          </Box>
        )}
        {query.error && (
          <Alert severity="error" action={<Button onClick={() => query.refetch()}>Tentar novamente</Button>}>
            Não foi possível carregar os registros.
          </Alert>
        )}
        {!query.isLoading && !query.error && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns[recurso].map((col) => (
                    <TableCell key={col.key}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={String(row.id ?? row.id_projetos)}>
                    {columns[recurso].map((col) => {
                      const value = row[col.key];
                      const text =
                        col.key === "permissao"
                          ? permissoesLabels[String(value)] || value
                          : col.key === "ativo"
                            ? value
                              ? "Ativo"
                              : "Inativo"
                            : col.key === "sucesso"
                              ? value
                                ? "Sucesso"
                                : "Falha"
                              : col.key === "data" || col.key.startsWith("data_")
                                ? formatarDataBrasilia(value as string)
                                : value;
                      return <TableCell key={col.key}>{String(text ?? "—")}</TableCell>;
                    })}
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns[recurso].length}>Nenhum registro encontrado.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={-1}
          page={page}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
          onPageChange={(_, next) => setPage(next)}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from }) =>
            items.length
              ? `${from}–${page * 10 + items.length}${query.data?.hasMore ? " (há mais registros)" : ""}`
              : "0 registros"
          }
          nextIconButtonProps={{ disabled: query.isFetching || !query.data?.hasMore }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
